import { useCallback } from "react";

import { captureException } from "src/lib/monitoring";
import { putWithProgress } from "src/utils/put-with-progress";

import { useInitiateChatFileUploadsMutation } from "src/graphql/types";
import type {
  UploadAttachment,
  UploadTarget,
} from "src/components/llm/utils/file-processing";

/** What the store asks for when it admits a selection. */
export type UploadTargetRequest = {
  fileName: string;
  /**
   * Omitted for an attachment whose bytes are fetched later (a library pick),
   * where the size isn't known yet. The server skips the size check for those
   * and enforces the real one when the upload is confirmed.
   */
  sizeBytes?: number;
};

// One request per selection leaves the mutation budget available for sending the message.
export const useInitiateChatFileUploads = (): ((
  files: UploadTargetRequest[],
) => Promise<UploadTarget[]>) => {
  // Minting changes no queryable data, so opt out of the global mutation
  // default that invalidates every active query.
  const { mutateAsync: initiateUploads } = useInitiateChatFileUploadsMutation({
    onSuccess: () => {},
  });

  return useCallback(
    async (files) => {
      if (files.length === 0) return [];
      const { initiateChatFileUploads: minted } = await initiateUploads({
        input: {
          files: files.map(({ fileName, sizeBytes }) => ({
            fileName,
            fileSizeBytes: sizeBytes ?? null,
          })),
        },
      });
      // Positional, not keyed by name: two attachments can share a filename,
      // and the server returns one target per request in the order given.
      return minted.files.map(({ uploadUrl, s3Key }) => ({
        uploadUrl,
        s3Key,
      }));
    },
    [initiateUploads],
  );
};

// Standalone callers mint a single target; chat selections share their batch's targets.
export const useUploadChatFile = (): UploadAttachment => {
  const mintUploadTargets = useInitiateChatFileUploads();

  return useCallback(
    async (file, options) => {
      const [target] = options?.target
        ? [options.target]
        : await mintUploadTargets([
            { fileName: file.name, sizeBytes: file.size },
          ]);
      if (!target) {
        throw new Error("The upload could not be prepared. Try again.");
      }

      try {
        await putWithProgress({
          url: target.uploadUrl,
          body: file,
          contentType: file.type,
          label: file.name,
          onProgress: options?.onProgress,
          signal: options?.signal,
        });
      } catch (error) {
        if (options?.signal?.aborted) throw error;
        captureException(error);
        throw new Error("Upload failed. Try again.", { cause: error });
      }

      return target.s3Key;
    },
    [mintUploadTargets],
  );
};
